import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Eye, EyeOff } from 'lucide-react';

export default function ClientLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, signInWithEmail, signInWithGoogle, signInWithMicrosoft, resetPassword } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/espace-client');
    }
  }, [isAuthenticated, navigate]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) throw error;
      
      toast({
        title: "Redirection vers Google",
        description: "Vous allez être redirigé vers Google pour vous connecter.",
      });
    } catch (error: any) {
      toast({
        title: "Erreur de connexion",
        description: error.message || "Impossible de se connecter avec Google",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setLoading(true);
    try {
      const { error } = await signInWithMicrosoft();
      if (error) throw error;
      
      toast({
        title: "Redirection vers Microsoft",
        description: "Vous allez être redirigé vers Microsoft pour vous connecter.",
      });
    } catch (error: any) {
      toast({
        title: "Erreur de connexion",
        description: error.message || "Impossible de se connecter avec Microsoft",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async () => {
    setLoading(true);
    try {
      if (resetMode) {
        const { error } = await resetPassword(email);
        if (error) throw error;
        
        toast({
          title: "E-mail envoyé",
          description: "Vérifiez votre boîte e-mail pour réinitialiser votre mot de passe.",
        });
        setResetMode(false);
      } else {
        const { error } = await signInWithEmail(email, password);
        if (error) throw error;
      }
    } catch (error: any) {
      toast({
        title: "Erreur de connexion",
        description: error.message || "Impossible de se connecter",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Espace Client</CardTitle>
          <CardDescription>
            {resetMode ? 'Réinitialiser votre mot de passe' : 'Connectez-vous à votre espace client'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* SSO Buttons */}
          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full"
              onClick={handleGoogleSignIn}
              disabled={loading}
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Se connecter avec Google
            </Button>
            
            <Button
              variant="outline"
              className="w-full"
              onClick={handleMicrosoftSignIn}
              disabled={loading}
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                <path fill="#f25022" d="M1 1h10v10H1z"/>
                <path fill="#00a4ef" d="M13 1h10v10H13z"/>
                <path fill="#7fba00" d="M1 13h10v10H1z"/>
                <path fill="#ffb900" d="M13 13h10v10H13z"/>
              </svg>
              Se connecter avec Microsoft
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Ou</span>
            </div>
          </div>

          {/* Email Login */}
          <div className="space-y-3">
            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
              />
            </div>

            {!resetMode && (
              <div>
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Votre mot de passe"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}

            <Button
              className="w-full"
              onClick={handleEmailSignIn}
              disabled={loading || !email || (!resetMode && !password)}
            >
              {loading ? 'Connexion...' : resetMode ? 'Envoyer le lien' : 'Se connecter par e-mail'}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full text-sm"
              onClick={() => setResetMode(!resetMode)}
            >
              {resetMode ? 'Retour à la connexion' : 'Mot de passe oublié ?'}
            </Button>

            {/* Demo Access */}
            <div className="border-t pt-4">
              <p className="text-xs text-center text-muted-foreground mb-2">
                Accès démonstration
              </p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={async () => {
                  try {
                    // Ensure demo user exists (edge function idempotent)
                    await fetch('https://symdojhcmtptpjipuwmp.functions.supabase.co/create-demo-user', { method: 'POST' });
                  } catch (_) {}

                  setEmail('demo@cosmos-wave.com');
                  setPassword('Demo123!');
                  setResetMode(false);
                  toast({ title: 'Identifiants pré-remplis', description: 'Cliquez sur Se connecter pour accéder au dashboard démo.' });
                }}
              >
                Utiliser le compte de démonstration
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}